/**
 * Shared navigation structure for the backoffice sidebar.
 * Used in BackofficeLayoutComponent and any page needing the backoffice sidebar
 * (e.g., assisted order form in backoffice context).
 */

// RbacModule type defined here (source of truth) to avoid circular imports.
// Re-exported from rbac.models.ts for backward compatibility.
export type RbacModule =
  | 'logistics_europe'
  | 'logistics_africa'
  | 'virtual_mailbox'
  | 'hr'
  | 'it'
  | 'finance'
  | 'customer_service'
  | 'marketing'
  | 'knowledge'
  | 'monitoring'
  | 'crm'
  | 'admin';

export interface NavItem {
  label: string;
  icon: string;
  route: string;
  queryParams?: Record<string, string>;
  /**
   * #2050 — rôles legacy à qui l'entrée est masquée. Drapeau RBAC éteint, une
   * section visible montre TOUTES ses entrées : sans ce masque, le support
   * verrait des entrées de sa section qu'il ne peut pas ouvrir (renvoi à la
   * connexion, #2009). Confort d'affichage, PAS un contrôle d'accès : la garde
   * de route et les règles décident.
   */
  hiddenFor?: string[];
}

export interface NavSection {
  key: string;
  label: string;
  icon: string;
  divider?: string;
  module?: RbacModule;  // RBAC module for permission filtering
  children: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  // ── Mon espace (toujours visible) ──
  {
    key: 'workspace', label: 'Mon espace', icon: 'fa-briefcase',
    children: [
      { label: 'Mon travail', icon: 'fa-tasks', route: 'my-work' },
      { label: 'Mes approbations', icon: 'fa-check-double', route: 'my-approvals' },
      // #2050 — RH en libre-service : fermée au support (création par callable `employee`).
      { label: 'Mes congés', icon: 'fa-umbrella-beach', route: 'leave-requests', hiddenFor: ['support'] },
      { label: 'Mon timesheet', icon: 'fa-stopwatch', route: 'timesheet', hiddenFor: ['support'] },
      { label: 'Ma semaine', icon: 'fa-calendar-week', route: 'timesheet-week', hiddenFor: ['support'] },
      { label: 'Kanban', icon: 'fa-columns', route: 'kanban' },
    ]
  },

  // ── Opérations ──
  {
    key: 'europe', label: 'Logistique Europe', icon: 'fa-truck', divider: 'Opérations', module: 'logistics_europe',
    children: [
      { label: 'Dashboard', icon: 'fa-tachometer-alt', route: 'logistics-dashboard' },
      { label: 'Colis (all)', icon: 'fa-box', route: 'shipments' },
      { label: 'Colis lourds', icon: 'fa-weight-hanging', route: 'heavy-parcels' },
      { label: 'Réception départ', icon: 'fa-dolly', route: 'reception' },
      { label: 'Scanner un colis', icon: 'fa-barcode', route: 'scan' },
      { label: 'Collectes à recaler', icon: 'fa-calendar-times', route: 'rebooking' },
      { label: 'Échecs création DPD', icon: 'fa-triangle-exclamation', route: 'dpd-failures' },
      { label: 'Préparation', icon: 'fa-clipboard-check', route: 'preparation' },
      { label: 'Préparer un groupage', icon: 'fa-cubes', route: 'expedition' },
      { label: 'Suivi des groupages', icon: 'fa-archive', route: 'containers' },
      { label: 'Partenaires expédition', icon: 'fa-plane', route: 'shipping-partners' },
    ]
  },
  {
    key: 'africa', label: 'Logistique Africa', icon: 'fa-globe-africa', module: 'logistics_africa',
    children: [
      { label: 'Réception arrivée', icon: 'fa-plane-arrival', route: 'congo-reception' },
      { label: 'Dégroupage', icon: 'fa-box-open', route: 'degroupage' },
      { label: 'Tri livraison', icon: 'fa-random', route: 'sorting' },
      { label: 'Livraisons', icon: 'fa-shipping-fast', route: 'deliveries' },
      { label: 'Partenaires livraison', icon: 'fa-handshake', route: 'partners' },
    ]
  },
  {
    key: 'vm', label: 'Virtual Mailbox', icon: 'fa-mailbox', module: 'virtual_mailbox',
    // #1046 \u2014 le tableau de bord et la liste de colis VM ont disparu : un colis
    // VM se suit dans \u00ab Colis \u00bb (les phases `vm_reception`/`vm_unidentified`
    // sont dans l'onglet d\u00e9di\u00e9 \u00ab VM \u2014 en stock \u00bb depuis #1153 \u2014 avant elles
    // \u00e9taient en \u00ab En collecte \u00bb, qui annon\u00e7ait un colis pas encore arriv\u00e9 ;
    // #1121 \u2014 `vm_consolidation` \u00e9tait une phase morte jamais \u00e9crite) et se
    // traite dans le backoffice
    // standard. Le VM ne garde en propre que la r\u00e9ception \u00e0 l'arriv\u00e9e, la fiche
    // compte/bo\u00eete, et les colis non identifi\u00e9s (#1045) \u2014 jusqu'ici accessibles
    // seulement depuis le tableau de bord supprim\u00e9.
    // #1122 / #1153 / #1837 — « Travail VM du jour » ouvre la section : c'est
    // l'INDEX des 7 files (attendus déclarés / non identifiés / reçus à traiter /
    // en stock / attente de paiement / devis expiré / payés à traiter), en
    // lecture seule. Il ne recrée pas le tableau de bord supprimé par #1046 :
    // il ne fait que compter et pointer vers les écrans qui agissent.
    // L'écran de TRAITEMENT (#1837) n'a pas d'entrée de menu : il s'ouvre depuis
    // un colis précis (file « Reçus, à traiter », fiche colis, écran de
    // réception), jamais dans le vide.
    children: [
      { label: 'Travail VM du jour', icon: 'fa-list-check', route: 'vm/work' },
      { label: 'Comptes clients', icon: 'fa-users', route: 'vm/accounts' },
      { label: 'R\u00e9ception colis', icon: 'fa-box-open', route: 'vm/register-package' },
      { label: 'Colis non identifi\u00e9s', icon: 'fa-circle-question', route: 'vm/unidentified' },
    ]
  },

  // ── Relation client ──
  {
    key: 'customer', label: 'Customer Service', icon: 'fa-headset', divider: 'Relation client', module: 'customer_service',
    children: [
      { label: 'Dashboard', icon: 'fa-tachometer-alt', route: 'customer-service' },
      { label: 'Tickets', icon: 'fa-ticket-alt', route: 'tickets' },
      { label: 'Nouveau ticket', icon: 'fa-plus-circle', route: 'tickets/new' },
      { label: 'Envoi assisté', icon: 'fa-hands-helping', route: 'envoi-assiste', hiddenFor: ['support'] },
      { label: 'Échecs notifications', icon: 'fa-bell-slash', route: 'notifications/failures', hiddenFor: ['support'] },
    ]
  },
  {
    key: 'marketing', label: 'Marketing', icon: 'fa-bullhorn', module: 'marketing',
    children: [
      { label: 'Codes promo', icon: 'fa-tags', route: 'promotions' },
      { label: 'Nouveau code', icon: 'fa-plus-circle', route: 'promotions/new' },
      { label: 'Analytics', icon: 'fa-chart-bar', route: 'promo-analytics' },
      { label: 'Partenaires', icon: 'fa-handshake', route: 'marketing-partners' },
      { label: 'Paiements partenaires', icon: 'fa-money-bill-wave', route: 'partner-payouts' },
      { label: 'Campagnes', icon: 'fa-paper-plane', route: 'campaigns' },
      { label: 'Nouvelle campagne', icon: 'fa-plus-circle', route: 'campaigns/new' },
      { label: 'Stats campagnes', icon: 'fa-chart-line', route: 'campaign-analytics' },
      // customer-profiles vit dans la section CRM (#602) — le doublon faisait
      // toggler l'étoile favori sur les deux entrées (clé = route)
    ]
  },

  // ── Services internes ──
  {
    key: 'hr', label: 'RH & Personnel', icon: 'fa-user-tie', divider: 'Services internes', module: 'hr',
    children: [
      { label: 'Dashboard RH', icon: 'fa-tachometer-alt', route: 'hr-dashboard' },
      { label: 'Annuaire', icon: 'fa-address-book', route: 'directory' },
      { label: 'Recrutement', icon: 'fa-user-plus', route: 'recruitment' },
      { label: 'Onboarding', icon: 'fa-user-graduate', route: 'onboarding' },
      { label: 'Templates onboarding', icon: 'fa-clipboard-list', route: 'onboarding-templates' },
      { label: 'Présence', icon: 'fa-clock', route: 'attendance' },
      { label: 'Rapports présence', icon: 'fa-chart-bar', route: 'attendance-reports' },
      { label: 'Congés à approuver', icon: 'fa-check-double', route: 'leave-approvals' },
      { label: 'Calendrier équipe', icon: 'fa-calendar-alt', route: 'leave-calendar' },
      { label: 'Gestion des soldes', icon: 'fa-balance-scale', route: 'leave-balances-admin' },
      { label: 'Timesheets à approuver', icon: 'fa-clipboard-check', route: 'timesheet-approvals' },
      { label: 'Historique RH', icon: 'fa-history', route: 'hr-audit-log' },
      { label: 'Analytics RH', icon: 'fa-chart-pie', route: 'hr-analytics' },
      { label: 'Départements', icon: 'fa-building', route: 'departments' },
      { label: 'Organigramme', icon: 'fa-sitemap', route: 'org-chart' },
      { label: 'Annonces', icon: 'fa-bullhorn', route: 'announcements' },
      { label: 'Équipe logistique', icon: 'fa-id-card', route: 'logistics-team' },
    ]
  },
  {
    key: 'it', label: 'IT Service', icon: 'fa-laptop-code', module: 'it',
    children: [
      { label: 'Catalogue de services', icon: 'fa-th-large', route: 'service-catalog' },
      { label: 'CMDB', icon: 'fa-sitemap', route: 'cmdb' },
      { label: 'Asset Management', icon: 'fa-box-open', route: 'assets' },
      { label: 'Fournisseurs', icon: 'fa-handshake', route: 'suppliers' },
      { label: 'Incidents', icon: 'fa-fire', route: 'incidents' },
      { label: 'Changes', icon: 'fa-exchange-alt', route: 'changes' },
      { label: 'Demandes', icon: 'fa-hand-paper', route: 'requests' },
      { label: 'Releases', icon: 'fa-rocket', route: 'releases' },
    ]
  },
  {
    key: 'finance', label: 'Finance', icon: 'fa-chart-line', module: 'finance',
    children: [
      { label: 'Dashboard', icon: 'fa-tachometer-alt', route: 'finance' },
      { label: 'Transactions', icon: 'fa-exchange-alt', route: 'finance/transactions' },
      { label: 'Saisir un cout', icon: 'fa-plus-circle', route: 'finance/costs/new' },
      { label: 'Résultat (P&L)', icon: 'fa-balance-scale', route: 'finance/profit-loss' },
    ]
  },

  // ── CRM ──
  {
    key: 'crm', label: 'CRM', icon: 'fa-handshake', module: 'crm',
    children: [
      { label: 'Comptes', icon: 'fa-building', route: 'crm/accounts' },
      { label: 'Profils clients', icon: 'fa-address-book', route: 'customer-profiles' },
    ]
  },

  // ── Outils & Pilotage ──
  {
    key: 'knowledge', label: 'Base de connaissances', icon: 'fa-book', divider: 'Outils & Pilotage', module: 'knowledge',
    children: [
      { label: 'Dashboard', icon: 'fa-tachometer-alt', route: 'knowledge' },
      { label: 'Articles', icon: 'fa-file-alt', route: 'knowledge/articles' },
      { label: 'Nouvel article', icon: 'fa-plus-circle', route: 'knowledge/new' },
      // #585 : la page existait mais n'était accessible qu'en URL directe
      { label: 'Paramètres', icon: 'fa-cog', route: 'knowledge/settings', hiddenFor: ['support'] },
    ]
  },
  {
    key: 'monitoring', label: 'Monitoring', icon: 'fa-chart-bar', module: 'monitoring',
    children: [
      { label: 'Dashboard Business', icon: 'fa-tachometer-alt', route: 'monitoring' },
      { label: 'Santé technique', icon: 'fa-heartbeat', route: 'monitoring/technical' },
    ]
  },
  {
    key: 'admin', label: 'Administration', icon: 'fa-shield-alt', module: 'admin',
    children: [
      { label: 'Initialisation donnees', icon: 'fa-database', route: 'admin/data-init' },
      { label: 'Configuration plateforme', icon: 'fa-sliders-h', route: 'admin/platform-config' },
      { label: 'Registre des collections', icon: 'fa-table', route: 'admin/collection-registry' },
    ]
  },

  // ── Portail Client (lien externe) ──
  {
    key: 'portal', label: 'Portail Client', icon: 'fa-globe',
    children: [
      { label: 'Ouvrir le portail', icon: 'fa-external-link-alt', route: '/portal' },
    ]
  },
];
